import _ from 'underscore'
import $ from 'jquery'
import session from '@/auth/models/session'
import storage from '@/utils/storage'
import { setResponsiveSpinnerColor } from '@/ladda-bundle'
import { create as createLadda, LaddaButton } from 'ladda'
import { exception } from '@/utils/logging'
import { loadScript } from '@/utils/script'
import { sleep } from '@/utils/promise'
import { trackUnreadZendeskWebWidgetMessages } from '@/services/analytics'

let spinner: LaddaButton | null = null

const zendesk: ZendeskMessagingWidget = (...args: unknown[]) => {
  if (window.zE) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: Type is enforced via signature of wrapper
    window.zE.apply(null, args)
  } else {
    // eslint-disable-next-line no-console
    console.error.apply(null, ['zE is not defined', ...args])
  }
}

const ZENDESK_WIDGET_SCRIPT_URL =
  'https://static.zdassets.com/ekr/snippet.js?key=c08dd44f-eebb-4da4-b516-483a3521df2f'
const ZENDESK_SCRIPT_ELEMENT_ID = 'ze-snippet'
const HELP_BUTTON_SELECTOR = '#zendesk-help-button'

/**
 * Proactive messages can be configured on either specific paths, or paths that contain a string.
 * `searchContains` can filter either path matching strategy with an additional (`AND`) condition.
 */
type ZendeskProactiveMessageConfig = (
  | {
      pathName: string
    }
  | {
      pathNameContains: string
    }
) & {
  searchContains?: string
}

// By default, the web widget is only loaded in after the user clicks on the Zendesk help button or on heavy traffic
// pages defined by our general pages recognized in pageDataUtils' `getPageType` method. This list consists of
// specific pages where the web widget will be loaded automatically some amount of time after the page has loaded, and
// proactive messaging will connect to the Zendesk backend right when the web widget loads.
const SPECIFIC_PROACTIVE_MESSAGE_ENABLED_PAGES: ZendeskProactiveMessageConfig[] =
  [
    // Expected to mirror Proactive Message config in Zendesk Admin
    { pathNameContains: 'wedding' },
    { pathNameContains: 'save-date-card' },
    { pathNameContains: 'holiday' },
    { pathNameContains: 'christmas' },
    { pathNameContains: 'new-year' },
    { pathNameContains: 'graduation' },
    { pathNameContains: 'diy' },
    { pathNameContains: 'photo-books' }
  ]

const STORAGE_KEY_IS_OPEN = 'zendeskIsOpen'
const STORAGE_KEY_LAST_ACTIVITY = 'zendeskLastActivity'
const STORAGE_KEY_LOGIN_JWT = 'zendeskLoginJwt'
/**
 * The amount of time until we consider the user and their Zendesk session to be idle.
 * The widget won't be eagerly loaded if the user is idle.
 * If this is too short, and the user visits another page, not eagerly loading could cause the user
 * to miss incoming chat messages.
 *
 * A 15-minute timeout is used because Customer Service will end chats after 5-10 minutes of inactivity.
 */
const RECENT_ACTIVITY_TIMEOUT_MS = 15 * 60 * 1000
const hasRecentActivity = (): boolean => {
  const lastActivityTimeMs =
    parseInt(storage.getItem(STORAGE_KEY_LAST_ACTIVITY) as string) || 0
  return lastActivityTimeMs > Date.now() - RECENT_ACTIVITY_TIMEOUT_MS
}
/**
 * Remembers when the user was last active.
 * Allows us to eagerly load the widget if there's recent activity, which is a proxy for guessing
 * whether there's an active conversation. Eager loading is needed for the user to receive messages.
 */
const updateRecentActivity = () =>
  storage.setItem(STORAGE_KEY_LAST_ACTIVITY, Date.now())

const isOpen = () => !!storage.getItem(STORAGE_KEY_IS_OPEN)
const setIsOpen = (open: boolean) => {
  storage.setItem(STORAGE_KEY_IS_OPEN, open)
  if (open) {
    updateRecentActivity()
  }
}

/** Opens the messaging Web Widget. */
export const open = async () => {
  await load()
  zendesk('messenger', 'open')
}

/** Closes the messaging Web Widget. */
export const close = () => zendesk('messenger', 'close')

/**
 * Returns a value for the Product Type dropdown custom field (id = 24747587).
 */
const getProductType = (): string | null => {
  const pageData = window._mktoPageData || {}
  if (pageData.majorCategory === 'majorWedding') {
    return 'product-wedding'
  } else if (pageData.minorCategory === 'minorCorporateHoliday') {
    return 'product-corporate'
  } else if (pageData.minorCategory === 'minorHoliday') {
    return 'product-holidays'
  } else if (pageData.majorCategory) {
    return 'product-other'
  } else {
    return null
  }
}

const setConversationFields = () => {
  const fields: ZendeskConversationField[] = []
  const productType = getProductType()
  if (productType) {
    fields.push({
      id: '24747587',
      value: productType
    })
  }
  /**
   * Extracts order number from hashes in the form `#order/ORDERNUMBER`.
   */
  const orderNumber = /^#order\/([A-Z0-9]+)$/.exec(location.hash)?.[1]
  if (orderNumber) {
    fields.push({
      id: '24577498',
      value: orderNumber
    })
  }
  /**
   * Extracts proof ID from path + hash, e.g.:
   *   - /eco/personalize#ABCDEFabcd1234567890_-/approve/1
   *   - /eco/personalize#ABCDEFabcd1234567890_-
   *   - /eco/personalize/editor/2#ABCDEFabcd1234567890_-/options/%7B"quantity"%3A1%7D
   *   - /eco/personalize/editor/2#ABCDEFabcd1234567890_-
   */
  const proofId =
    /personalize(?:\/editor\/[0-9]+)?#([A-Za-z0-9_-]{22})\/?/.exec(
      location.pathname + location.hash
    )?.[1]
  if (proofId) {
    fields.push({
      id: '7638749509783',
      value: proofId
    })
  }

  if (fields.length) {
    zendesk('messenger:set', 'conversationFields', fields)
  }
}

/**
 * Gets a Zendesk Messaging JWT from Mondrian for the currently signed in Paper Culture user.
 */
const getJwt = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (session.isSignedIn()) {
      $.ajax({
        url: '/api/v2/zendesk/messaging/jwt',
        authenticate: true
      }).then(resolve, reject)
    } else {
      reject(new Error('Cannot get Zendesk messaging JWT if not signed in'))
    }
  })
}

const getCachedJwt = (): string | null =>
  (storage.getItem(STORAGE_KEY_LOGIN_JWT) as string | undefined) ?? null

const removeCachedJwt = (): void => storage.removeItem(STORAGE_KEY_LOGIN_JWT)

const setCachedJwt = (jwt: string) =>
  storage.setItem(STORAGE_KEY_LOGIN_JWT, jwt)

type ZendeskMessagingJwtClaims = {
  /** Client ID as a string */
  external_id: string
  email: string
  /** Expiration time of the JWT as unix time */
  exp: number
  /** Full name of the registered user */
  name: string
  scope: 'user'
}

/**
 * Extract the JWT Claims from the JWS Payload. The first part is the header.
 */
const getJwtClaims = (jwt: string): ZendeskMessagingJwtClaims =>
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  JSON.parse(atob(jwt.split('.')[1]!))

/**
 * Returns true if the JWT is not expired.
 */
const isJwtValid = (jwt: string): boolean => {
  try {
    return getJwtClaims(jwt).exp > Date.now() / 1000
  } catch (e) {
    exception(e)
    return false
  }
}

/**
 * Returns a cached version of the JWT if it is valid,
 * otherwise fetches a new Zendesk JWT using the Paper Culture JWT, caches it, and returns it.
 *
 * This should only be called if the user is signed in.
 */
const getValidJwt = async (): Promise<string> => {
  let jwt = getCachedJwt()
  if (!jwt || !isJwtValid(jwt)) {
    removeCachedJwt()
    jwt = await getJwt()
    setCachedJwt(jwt)
  }
  return jwt
}

const unsetUser = (): void => {
  removeCachedJwt()
  zendesk('messenger', 'logoutUser')
}

/**
 * Sets the current user in the Zendesk SDK using a valid Zendesk Messaging JWT.
 *
 * Should only be called if the user is signed in.
 */
const setUser = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    zendesk('messenger', 'loginUser', (callback) => {
      getValidJwt().then(callback).then(resolve, reject)
    })
  })
}

/**
 * Updates the authenticated user for Zendesk Messaging.
 *
 * Zendesk Messaging uses its own JWT, which we generate from our own backend using a valid Paper Culture JWT.
 * Calling this method will sync up the widget's logged-in user with the current Paper Culture session.
 *
 * If there is no active Paper Culture session, this method will unset the Zendesk Messaging user,
 * which will clear all history.
 */
export const authenticate = async function (): Promise<void> {
  const zendeskJwt = getCachedJwt()
  const signedIn = session.isSignedIn()
  // Always defined if signed in
  const sessionEmail = session.get('email')

  if (
    zendeskJwt &&
    (!signedIn || (signedIn && sessionEmail !== getJwtClaims(zendeskJwt).email))
  ) {
    // Log out the Zendesk session if there's a cached JWT and the user is on longer signed in,
    // or is signed in as another user.
    unsetUser()
  }
  if (signedIn) {
    // User must be set every time the SDK is initialized.
    // It doesn't remember who's signed in across page loads.
    await setUser()
  }
}

const load = _.once(async () => {
  await loadScript(ZENDESK_WIDGET_SCRIPT_URL, {
    id: ZENDESK_SCRIPT_ELEMENT_ID
  })
  // Sync Zendesk authentication state with the Paper Culture session.
  session.on('change:token', () =>
    session.isSignedIn() ? setUser() : unsetUser()
  )
  // Keep track of whether a chat session is in progress, based on whether there are unread messages
  zendesk('messenger:on', 'unreadMessages', (count) => {
    if (count) {
      // Checking count prevents tracking activity when the count is 0 during initialization.
      // New messages will trigger the callback with a positive count.
      // If the widget is visible, the callback will be fired again with 0.
      updateRecentActivity()
      trackUnreadZendeskWebWidgetMessages({ count })
    }
  })
  zendesk('messenger:on', 'open', () => {
    setIsOpen(true)
    stopSpinner()
  })
  zendesk('messenger:on', 'close', () => setIsOpen(false))
  setConversationFields()
  await authenticate()
})

const startSpinner = () => {
  const button = $(HELP_BUTTON_SELECTOR)[0] as HTMLButtonElement
  setResponsiveSpinnerColor(button)
  spinner = createLadda(button).start()
}

const stopSpinner = () => {
  if (spinner) {
    spinner.stop()
    // The spinner only shows up once while the web widget is being loaded.
    spinner.remove()
    spinner = null
  }
}

/**
 * Launches the web widget: loads the script if it hasn't already been loaded, and opens up the component.
 */
export const launch = async (
  options: {
    skipSpinnerAtLoad?: boolean
  } = {}
) => {
  // Load the script without showing the spinner
  if (!options.skipSpinnerAtLoad) {
    startSpinner()
  }
  let error = false
  try {
    await open()
  } catch (_) {
    // Ad blocker may be preventing the Zendesk Web Widget SDK from loading.
    // Open the help center instead.
    window.open('https://help.paperculture.com/hc/en-us', '_blank')
    error = true
  } finally {
    if (!options.skipSpinnerAtLoad) {
      // If the widget is not open, and there was no error, the spinner will be stopped on open callback instead.
      // The initial script loads fairly quickly, but there's still a delay before the main widget
      // code loads and the UI is updated.
      // By deferring `stopSpinner()` until the on open callback, the user will see the spinner for longer,
      // reducing (but still not eliminating) the time that nothing appears to be happening.
      if (isOpen() || error) {
        stopSpinner()
      }
    }
  }
}

/**
 * Returns `true` if the current page should have proactive messaging enabled by default.
 * Page-specific overrides (defined in global settings) may override this.
 */
const isProactiveMessageEnabledPage = (): boolean => {
  // Looks for an exact match on path name.
  // But if search is specified, check if the search is present, instead of an exact match.
  const isSpecificProactiveMessageEnabledPage =
    SPECIFIC_PROACTIVE_MESSAGE_ENABLED_PAGES.some(
      ({ searchContains, ...config }) =>
        ('pathName' in config
          ? config.pathName === window.location.pathname
          : window.location.pathname.includes(config.pathNameContains)) &&
        (searchContains === undefined ||
          window.location.search.includes(searchContains))
    )

  // By default, we will eagerly load the widget on each of these page types, but at the expense of
  // reduced page load performance, which could degrade the user experience and SEO.
  const isGeneralProactiveMessageEnabledPage = () => {
    /*
    switch (pageDataUtils.getPageType()) {
      // Add cases to enable proactive messaging on selected page types
      case 'account':
        return true
      default:
        return false
    }
    */
    // Enable specific page types above if we activate proactive messaging in the future
    return false
  }

  return (
    isSpecificProactiveMessageEnabledPage ||
    isGeneralProactiveMessageEnabledPage()
  )
}

export const initialize: () => Promise<void> = _.once(() => {
  window.pc = window.pc || {}
  window.pc.settings = window.pc.settings || {}
  window.pc.settings.scripts = window.pc.settings.scripts || {}
  window.pc.settings.scripts.zendeskWebWidget =
    window.pc.settings.scripts.zendeskWebWidget || {}

  // If there's already a setting to force `true` or `false`, it will override defaults
  if (
    typeof window.pc.settings.scripts.zendeskWebWidget
      .isProactiveMessageEnabled !== 'boolean'
  ) {
    window.pc.settings.scripts.zendeskWebWidget.isProactiveMessageEnabled =
      isProactiveMessageEnabledPage()
  }

  // Enable by default
  const isEnabled =
    window.pc.settings.scripts.zendeskWebWidget.enabled !== false
  const { isProactiveMessageEnabled, isEagerLoad } =
    window.pc.settings.scripts.zendeskWebWidget

  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    if (isEnabled) {
      $(HELP_BUTTON_SELECTOR).on('click', () => launch())

      let loadImmediately = isEagerLoad
      if (!loadImmediately && isProactiveMessageEnabled) {
        loadImmediately = true
      }

      if (hasRecentActivity() || isOpen()) {
        // Load immediately to ensure messages are received ASAP or that the chat feels persistent
        load().then(resolve, reject)
      } else if (loadImmediately) {
        // Defer loading to prioritize other resources
        $(window).on('load', () => {
          sleep(5000).then(load).then(resolve, reject)
        })
      } else {
        // By default, don't load or launch until clicked on
        resolve()
      }
    } else {
      // Not enabled, so there's nothing to do
      resolve()
    }
  })
})
